import { Formik } from 'formik';
import propTypes from 'prop-types';
import { useContext } from 'react';
import { object, string } from 'yup';
import {
  SITE_LIVINGSOCIAL_IE,
  BRAND_WOWCHER,
  SITE_WOWCHERIE,
} from '../../config/setup/setup';
import {
  REQUIRED_FIELD,
  SEND_GIFT_PACK,
  YOUR_NAME,
  GIFTEE_NAME,
  GIFTEE_ADDRESS,
  REGISTER_ENTER_VALID_POSTCODE,
  YOUR_MESSAGE,
  VOUCHER_PACK_MESSAGE,
  VOUCHER_PACK_SENT,
  VOUCHER_GIFTPACK_MESSAGE,
  GENERIC_FAILURE,
} from '../../config/text/text';
import ErrorTrackingService from '../../helpers/errorTrackingService';
import { GiftOptionType, sendGift } from '../../helpers/gifting';
import { validatePostCode } from '../../helpers/validations';
import ThemeContext from '../../providers/ThemeProvider';
import Button from '../_generic/button/CTAButton';
import FormikInput from '../_generic/input/FormikInput';
import FormikTextarea from '../_generic/input/FormikTextarea';
import Spinner from '../_generic/spinner/Spinner';
import useToast from '../_generic/toast/UseToast';
import BillingAddress from '../address/BillingAddress';
import { DEFAULT_EVENT_KEY } from './constants';

const schema = (LS) =>
  object().shape({
    addressLine1: string().required(REQUIRED_FIELD),
    addressLine2: string(),
    city: string().required(REQUIRED_FIELD),
    name: string().required(REQUIRED_FIELD),
    postCode: LS
      ? string()
      : string()
          .required(REQUIRED_FIELD)
          .test('postCode', REGISTER_ENTER_VALID_POSTCODE, (value) =>
            validatePostCode(value),
          ),

    senderName: string().required(REQUIRED_FIELD),
  });

const VoucherGiftPackForm = ({ voucher, setToggle, mutate }) => {
  const theme = useContext(ThemeContext);
  const toast = useToast();
  const site = process.env.NEXT_PUBLIC_SITE;
  const livingsocialie =
    site === SITE_LIVINGSOCIAL_IE || site === SITE_WOWCHERIE;
  const Environment = process.env.NEXT_PUBLIC_BRAND || BRAND_WOWCHER;

  const extraClasses = `mobile-highlight-border ${
    Environment !== BRAND_WOWCHER && 'lowercase_placeholder'
  }`;

  const sendForm = async (values) => {
    try {
      await sendGift({
        giftOptionType: GiftOptionType.giftPack,
        values,
        voucher,
      });
      await mutate();
      setToggle(DEFAULT_EVENT_KEY);
      toast.addToast(VOUCHER_GIFTPACK_MESSAGE, 'toast-success');
    } catch (error) {
      toast.addToast(GENERIC_FAILURE, 'toast-error');
      ErrorTrackingService.logError(error);
    }
  };

  return (
    <>
      <div className="form gift-pack-form">
        <h2 className="title">{SEND_GIFT_PACK}</h2>
        <Formik
          enableReinitialize
          initialValues={{
            addressLine1: voucher.gift?.recipientAddressLine1 || '',
            addressLine2: voucher.gift?.recipientAddressLine2 || '',
            city: voucher.gift?.recipientCity || '',
            message: voucher.gift?.giftMessage || '',
            name: voucher.gift?.recipientName || '',
            postCode: voucher.gift?.recipientPostCode || '',
            senderName: voucher.gift?.senderName || '',
          }}
          onSubmit={sendForm}
          validateOnBlur
          validateOnChange
          validateOnMount
          validationSchema={schema(livingsocialie)}
        >
          {({
            handleChange,
            handleSubmit,
            touched,
            handleBlur,
            isSubmitting,
            isValid,
            setValues,
            values,
            errors,
          }) => {
            return (
              <form name="send-gift-pack" onSubmit={handleSubmit}>
                {isSubmitting && <Spinner display="absolute" />}
                <FormikInput
                  disabled={voucher.gifted}
                  errorText={errors.senderName}
                  extraClasses={extraClasses}
                  inputError={errors.senderName}
                  inputTouched={touched.senderName}
                  inputValue={values.senderName}
                  name="senderName"
                  onBlurFunction={handleBlur}
                  onChangeFunction={handleChange}
                  placeholder={`${YOUR_NAME}*`}
                  required
                  type="text"
                />
                <FormikInput
                  bottomLined
                  disabled={voucher.gifted}
                  errorText={errors.name}
                  extraClasses={extraClasses}
                  inputError={errors.name}
                  inputTouched={touched.name}
                  inputValue={values.name}
                  name="name"
                  onBlurFunction={handleBlur}
                  onChangeFunction={handleChange}
                  placeholder={`${GIFTEE_NAME}*`}
                  required
                  type="text"
                />
                <div className="row">
                  <BillingAddress
                    disabled={voucher.gifted}
                    errors={errors}
                    extraClasses={extraClasses}
                    lookupAddressText={`${GIFTEE_ADDRESS}*`}
                    onBlurFunction={handleBlur}
                    onChangeFunction={handleChange}
                    setValues={setValues}
                    showAllFields
                    site={site}
                    touched={touched}
                    values={values}
                  />
                </div>
                <FormikTextarea
                  bottomLined
                  disabled={voucher.gifted}
                  errorText={errors.message}
                  inputError={errors.message}
                  inputTouched={touched.message}
                  inputValue={values.message}
                  maxLength={175}
                  name="message"
                  onBlurFunction={handleBlur}
                  onChangeFunction={handleChange}
                  placeholder={YOUR_MESSAGE}
                />
                <p className="required">*{REQUIRED_FIELD}</p>
                <div className="submit-button">
                  <Button
                    className="full-width"
                    disabled={isSubmitting || !isValid || voucher.gifted}
                    primaryAction={Environment !== BRAND_WOWCHER}
                    secondaryButton={Environment === BRAND_WOWCHER}
                    type="submit"
                  >
                    {SEND_GIFT_PACK}
                  </Button>
                </div>
                <p className="message">
                  {VOUCHER_PACK_MESSAGE(voucher.giftType)}
                </p>
                {voucher.gifted && <p>{VOUCHER_PACK_SENT}</p>}
              </form>
            );
          }}
        </Formik>
      </div>
      <style jsx>{`
        .title {
          color: ${theme.colors.primary};
          font-weight: normal;
          font-size: 1.7rem;
          margin-bottom: 15px;
        }
        .required {
          font-size: 0.8em;
          text-align: right;
          margin-bottom: 15px;
        }
        .submit-button {
          margin-bottom: 15px;
        }
        .gift-pack-form :global(.input-container .form-control[disabled]) {
          border-bottom-color: #d4d4d4;
        }
      `}</style>
    </>
  );
};

VoucherGiftPackForm.propTypes = {
  mutate: propTypes.func,
  setToggle: propTypes.func.isRequired,
  voucher: propTypes.object,
};

export default VoucherGiftPackForm;
